import { ArrowRight } from "lucide-react";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

export default function Trust() {
  return (
    <>
      <SEO
        title="Trust — security, privacy, data handling — WhoBrew"
        description="How WhoBrew handles your data, your card visitors' privacy, and the security of the systems behind your card. Plain-language answers to the questions security teams actually ask."
        canonical={canonicalFor("/trust")}
      />

      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Trust</Eyebrow>
          <Display as="h1" scale="lg" italicWord="data" className="mt-5">
            How we handle your data, your visitors, and your card.
          </Display>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            Plain-language answers to the security and privacy questions that actually matter.
            No jargon, no marketing copy dressed up as policy. If your security team needs more,
            email{" "}
            <a
              href="mailto:support@whobrew.us"
              className="text-coffee-900 dark:text-cream-50 dark:text-cream-50 underline underline-offset-4 decoration-latte-400 hover:decoration-coffee-900 dark:hover:decoration-cream-50"
            >
              support@whobrew.us
            </a>{" "}
            and we'll send the long version.
          </p>
        </Container>
      </Section>

      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <div className="space-y-16">
            <TrustSection
              eyebrow="Hosting"
              title="Where your card lives."
              body={
                <>
                  <p>
                    The WhoBrew application runs on Netlify (frontend) and Railway (backend), both
                    on managed cloud infrastructure with HTTPS-only traffic, automatic certificate
                    rotation, and a hardened deployment pipeline. The database is a managed
                    MySQL instance with daily encrypted backups and point-in-time recovery.
                  </p>
                  <p>
                    User data and card content live in the United States. We do not replicate user
                    data outside the US.
                  </p>
                </>
              }
            />

            <TrustSection
              eyebrow="Authentication"
              title="How sign-in works."
              body={
                <>
                  <p>
                    WhoBrew uses Clerk for authentication. Sign-in is handled by Clerk's hosted
                    flow with email magic links, password sign-in, and OAuth via Google. Passwords
                    are never stored on WhoBrew's servers. Sessions are signed with rotating keys
                    and have a configurable lifetime.
                  </p>
                  <p>
                    Two-factor authentication is supported and recommended for any account that
                    represents a team or company.
                  </p>
                </>
              }
            />

            <TrustSection
              eyebrow="Payments"
              title="Where your billing data lives."
              body={
                <>
                  <p>
                    All payments are processed by Stripe. Card numbers never touch WhoBrew's
                    servers. We store only Stripe customer IDs and subscription status. PCI
                    compliance is inherited from Stripe.
                  </p>
                  <p>
                    Refunds, plan changes, and cancellations are self-serve from your account
                    settings via the Stripe Customer Portal.
                  </p>
                </>
              }
            />

            <TrustSection
              eyebrow="Card visitors"
              title="What we know about people who view your card."
              body={
                <>
                  <p>
                    When someone opens your WhoBrew card — by URL, QR, or NFC tap — we record the
                    visit so you can see your visit counts and link clicks in Insights. We do not
                    place third-party trackers, do not run advertising pixels, and do not sell or
                    transfer visitor data to anyone.
                  </p>
                  <p>
                    Visitors are not identified by name. We see device type (iPhone vs Android),
                    coarse location (city-level, derived from IP for analytics), and which links
                    on your card got clicked. Personally identifying information about your card
                    visitors only enters the system when a visitor deliberately fills out your
                    contact form, in which case the data is yours and yours alone.
                  </p>
                </>
              }
            />

            <TrustSection
              eyebrow="Your data"
              title="Export, delete, or take it with you."
              body={
                <>
                  <p>
                    You can export your card data, your leads, and your insights as CSV at any
                    time. You can delete your account from the app, which permanently removes
                    your card content, your leads, and your account record from the production
                    database within 30 days, and from backups within 90.
                  </p>
                  <p>
                    GDPR and CCPA data subject requests can be sent to{" "}
                    <a
                      href="mailto:support@whobrew.us"
                      className="text-coffee-900 dark:text-cream-50 dark:text-cream-50 underline underline-offset-4 decoration-latte-400 hover:decoration-coffee-900 dark:hover:decoration-cream-50"
                    >
                      support@whobrew.us
                    </a>
                    . We process them within 30 days as required by both regulations.
                  </p>
                </>
              }
            />

            <TrustSection
              eyebrow="Reporting issues"
              title="If you find a security issue."
              body={
                <p>
                  Email{" "}
                  <a
                    href="mailto:support@whobrew.us?subject=Security%20report"
                    className="text-coffee-900 dark:text-cream-50 dark:text-cream-50 underline underline-offset-4 decoration-latte-400 hover:decoration-coffee-900 dark:hover:decoration-cream-50"
                  >
                    support@whobrew.us
                  </a>{" "}
                  with the subject "Security report." We acknowledge within 24 hours, ship a
                  fix or a workaround as quickly as the severity warrants, and credit reporters
                  publicly if you'd like the credit. We do not yet run a formal bug bounty, but
                  we do pay meaningful thank-yous for responsible disclosure.
                </p>
              }
            />
          </div>
        </Container>
      </Section>

      <Hairline />

      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="answers" className="mx-auto max-w-3xl">
            Need more answers?
          </Display>
          <div className="mt-10 flex justify-center">
            <Button href="mailto:support@whobrew.us" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Email support
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

function TrustSection({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Display as="h2" scale="sm" className="mt-3 mb-6">{title}</Display>
      <div className="space-y-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
        {body}
      </div>
    </div>
  );
}
